import { useEffect, useState } from 'react';
import { Field, SearchIcon, cx } from '../ui';
import styles from './gridSearch.module.css';

/**
 * The grid's search control: an icon until it is needed, the field while it is being typed
 * in, and the icon again on blur — wearing the accent while a search is set, so a narrowed grid
 * does not read as rows that vanished by themselves. Lifted out of the manage-subscriptions page,
 * which grew it first, once every grid wanted one.
 *
 * The icon stays put on the row's right edge and the bar grows out beside it, so the control the
 * reader pressed is still under the pointer when the field appears — and pressing it again applies
 * the search: it submits where a caller gave the search somewhere to go (`onSubmit`), and simply
 * folds the bar away everywhere else, where the value already filtered the grid on every keystroke.
 *
 * The words come from the caller (`ui`'s rule, `Modal.closeLabel`'s shape) — most screens pass the
 * shared `listing.search` group, the manage-subscriptions page its own more specific one.
 *
 * The ✕'s mousedown is prevented (`Field`), so clearing does not blur-collapse the box; Escape
 * clears first (`Field` again) and only an Escape over an empty box hands focus back and closes it.
 * The icon's own mousedown is prevented too while the bar is open, for the same reason — without
 * it the press blurs the field, the blur collapses the bar, and the click then re-opens it.
 */
export interface GridSearchWords {
  /** The control's accessible name — "Search this list". */
  readonly label: string;
  /** The collapsed icon's name while a search is set — the reason the grid looks short. */
  readonly activeTitle: (q: string) => string;
  readonly placeholder: string;
  readonly clear: string;
  /** The in-bar icon's name while the bar is open — what pressing it does. */
  readonly submit: string;
}

export function GridSearch({
  id,
  value,
  onChange,
  words,
  onSubmit,
  onClear,
  className,
}: {
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly words: GridSearchWords;
  /** Where searching is a navigation rather than a display-only narrowing. */
  readonly onSubmit?: () => void;
  /**
   * The ✕, where the search is server-side: pressing it removes the *applied* filter — a
   * navigation back to the unfiltered list — where emptying the text by hand only applies on
   * submit. Display-only grids leave this unset; their every keystroke already filters.
   */
  readonly onClear?: () => void;
  readonly className?: string;
}) {
  const [open, setOpen] = useState(false);
  const active = value.trim() !== '';

  useEffect(() => {
    if (open) document.getElementById(id)?.focus();
  }, [id, open]);

  const iconTitle = open ? words.submit : active ? words.activeTitle(value.trim()) : words.label;

  return (
    <div className={cx(styles.root, className)}>
      {open ? (
        <Field
          id={id}
          label={words.label}
          hideLabel
          type="search"
          value={value}
          onChange={onChange}
          placeholder={words.placeholder}
          clear={{ label: words.clear, onClear }}
          className={styles.field}
          onSubmit={onSubmit}
          onBlur={() => setOpen(false)}
          onKeyDown={(key) => {
            // Escape over an *empty* box closes it; with text in it, Field's own Escape clears first.
            if (key === 'Escape' && value === '') {
              setOpen(false);
              return true;
            }
            return false;
          }}
        />
      ) : null}
      <button
        type="button"
        className={cx(styles.icon, active && styles.iconActive)}
        title={iconTitle}
        onMouseDown={(event) => {
          if (open) event.preventDefault();
        }}
        onClick={() => {
          if (!open) setOpen(true);
          else if (onSubmit !== undefined) onSubmit();
          else setOpen(false);
        }}
      >
        <SearchIcon />
        <span className={styles.srOnly}>{iconTitle}</span>
      </button>
    </div>
  );
}
